/**
 * Single source of truth for variant option axes: normalize keys/values at write time
 * and derive storefront/admin product_options from variant.attributes only.
 */

import { normalizeOptionKey } from '@/lib/aliexpress-sku-attrs'

const SIZE_TOKEN =
  /^(xxs|xs|s|m|l|xl|xxl|xxxl|2xl|3xl|4xl|5xl|6xl|7xl|one[\s-]?size|os|std|standard)$/i

/** Canonical public attribute keys (Colour / Couleur → Color, Taille → Size). */
export function canonicalVariantAttributeKey(rawKey: string): string {
  const nk = normalizeOptionKey(rawKey)
  if (!nk) return ''
  const lower = nk.toLowerCase()
  if (lower === 'colour' || lower === 'couleur') return 'Color'
  if (lower === 'taille') return 'Size'
  return nk
}

function normalizeDisplayValueForAxis(canonicalKey: string, raw: string): string {
  let t = String(raw ?? '')
    .trim()
    .replace(/\s+/g, ' ')
  if (!t) return ''
  if (canonicalKey.toLowerCase() === 'size' && SIZE_TOKEN.test(t)) return t.toUpperCase()
  return t
}

const INTERNAL_ATTR_KEYS = new Set(['__ae_matrix', '__ae_vid'])

function isPreservedInternalKey(k: string): boolean {
  return INTERNAL_ATTR_KEYS.has(k)
}

/**
 * Normalize variant.attributes before DB write:
 * - Canonicalize public keys (Color, Size, …)
 * - Trim / collapse spaces; uppercase common size tokens
 * - Drop unknown leading-underscore keys except __ae_matrix / __ae_vid
 */
export function normalizeVariantAttributesForWrite(raw: Record<string, string>): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {}
  const internal: Record<string, string> = {}
  const bucket = new Map<string, { key: string; value: string }>()

  for (const [k0, v0] of Object.entries(raw)) {
    if (!k0) continue
    if (k0.startsWith('_')) {
      if (isPreservedInternalKey(k0)) {
        const iv = String(v0 ?? '').trim()
        if (iv) internal[k0] = iv
      }
      continue
    }
    const ck = canonicalVariantAttributeKey(k0)
    if (!ck) continue
    const val = normalizeDisplayValueForAxis(ck, String(v0 ?? ''))
    if (!val) continue
    bucket.set(ck.toLowerCase(), { key: ck, value: val })
  }

  const publicObj = Object.fromEntries([...bucket.values()].map((x) => [x.key, x.value]))
  return Object.fromEntries(
    Object.entries({ ...publicObj, ...internal }).sort(([a], [b]) => a.localeCompare(b)),
  )
}

export type VariantLikeForOptions = { attributes?: Record<string, string> | null }

/**
 * Build product_options axes from variant rows only (max 3 axes, same as DB constraint).
 */
export function deriveProductOptionsFromVariantRows(
  variants: VariantLikeForOptions[],
): { name: string; values: string[] }[] {
  if (!Array.isArray(variants) || variants.length === 0) return []
  const byAxis = new Map<string, Set<string>>()

  for (const row of variants) {
    const attrs = normalizeVariantAttributesForWrite((row.attributes || {}) as Record<string, string>)
    for (const [k, v] of Object.entries(attrs)) {
      if (k.startsWith('_')) continue
      if (!v) continue
      if (!byAxis.has(k)) byAxis.set(k, new Set())
      byAxis.get(k)!.add(v)
    }
  }

  const rank = (name: string) => {
    const lower = name.toLowerCase()
    if (lower === 'color') return 0
    if (lower === 'size') return 1
    return 2 + name.localeCompare('')
  }

  const axes = [...byAxis.keys()].sort((a, b) => {
    const dr = rank(a) - rank(b)
    if (dr !== 0) return dr
    return a.localeCompare(b)
  })

  return axes.slice(0, 3).map((name) => ({
    name,
    values: [...(byAxis.get(name) || [])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
  }))
}
